'use client';

import {
    FetcherContext,
    useCalculationDetails,
    useCalculatorInputOptions,
} from '@copmer/calculator-integration/client';
import { formatNumberWithCommas } from '@copmer/calculator-integration/shared';
import { VoyageResult } from '@copmer/frc-api-types';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import Link from 'next/link';
import { useMemo } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getCalculatorAPIRoutes } from '@/freight/api-routes';
import { useClientBookableVessels } from '@/hooks/useClientBookableVessels';
import { reactQueryFetcher } from '@/lib/calculator/react-query-fetcher';
import { isSet } from '@/lib/is-set';
import { WithQueryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';

import { ColumnDefinition, MobileResultCard, columns, renderCardColumn } from './CalculationResults';
import { useCalculatorContextProvideValue } from './context';
import {
    CalculatorCurrencyProvider,
    useCalculatorCurrency,
    useCalculatorCurrencySign,
    useConvertedPlan,
} from './currency-context';
import { DebugTableKeyVal } from './DebugTable';
import Icon from './Icon';
import MobileDebugTable from './MobileDebugTable';
import { valueSuffixConstructor } from './utils';

interface ErrorProps {
    error: string | Error | { message: string };
    label?: string;
    pre?: boolean;
}

export const ErrorComponent = ({ error, label = 'Error', pre }: ErrorProps) => {
    const errorText = typeof error === 'string' ? error : error.message;

    return (
        <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>{label}</AlertTitle>
            <AlertDescription>
                <span style={pre ? { whiteSpace: 'pre' } : {}}>{errorText}</span>
            </AlertDescription>
        </Alert>
    );
};

function PlanDetailsPageInner({
    resultId,
    planId,
    fullPage,
    backToQuotePage,
    bookable,
}: {
    resultId: string;
    planId: string;
    fullPage?: boolean;
    backToQuotePage?: boolean;
    bookable?: boolean;
}) {
    const apiRoutes = useMemo(() => {
        return getCalculatorAPIRoutes();
    }, []);

    const contextValue = useCalculatorContextProvideValue();

    const { bookableVessels } = useClientBookableVessels();

    const {
        vesselMap,
        waterTypes,

        isApiLoaded,
    } = useCalculatorInputOptions(contextValue.apiRoutes, null, false);

    const { data: rawData, isLoading, error } = useCalculationDetails(resultId, apiRoutes.calculationDetails);

    const currency = useCalculatorCurrency();
    const currencySign = useCalculatorCurrencySign();

    const rawPlan = useMemo(
        () => (rawData?.data?.plans ?? []).find((p: VoyageResult) => p.id === planId),
        [rawData, planId]
    );
    const plan = useConvertedPlan(rawPlan);

    let body = null;

    if (error) {
        body = <ErrorComponent error={error} />;
    } else if (isLoading || !isApiLoaded) {
        body = (
            <div className="h-72 animate-pulse w-full flex flex-col p-4 gap-4">
                <div className="flex w-full gap-5 justify-between opacity-25">
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>

                    <div className="h-12 w-[40%] flex relative self-end">
                        <div className="w-1/2" />
                        <div className="flex flex-col gap-2 w-1/2 items-end">
                            <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                            <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                        </div>
                    </div>
                </div>

                <div className="flex w-full gap-5 justify-between opacity-25">
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                    <div className=" w-[40%]" />
                </div>

                <div className="mt-4 border-t border-b border-frGrey-700 text-frGrey-400 opacity-75 p-4 h-12 flex items-center justify-center">
                    Loading...
                </div>

                <div className="flex w-full gap-5 justify-between opacity-25">
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                    <div className="flex flex-col gap-2 w-1/5">
                        <div className="h-5 bg-frGrey-300 rounded-lg w-2/3"></div>
                        <div className="h-4 bg-frGrey-400 rounded-lg w-full"></div>
                    </div>
                </div>
            </div>
        );
    } else if (!plan) {
        body = <ErrorComponent error="Plan not found" label="Not found" />;
    } else {
        const showWithinContract = isSet(plan.debugData.inputParams.tolerance);

        const renderColumn = (column: ColumnDefinition, isMobile: boolean) =>
            renderCardColumn(column, isMobile, plan, vesselMap, 0);

        let isBookable = false;
        if (bookable !== undefined) {
            isBookable = bookable;
        } else if (bookableVessels !== undefined) {
            isBookable = bookableVessels.includes(plan.shipSize);
        } else {
            isBookable = false;
        }

        body = (
            <MobileResultCard
                columns={columns.filter((col) => {
                    if (col.key === 'isWithinTolerance') {
                        return showWithinContract;
                    }
                    return true;
                })}
                calculationId={resultId}
                planId={planId}
                currency={currency}
                renderColumn={renderColumn}
                isBookable={isBookable}
                backToQuotePage={backToQuotePage}
                isPage
                extra={
                    <>
                        <div className="pt-4 [grid-area:none]" tabIndex={0} />

                        <div className="pt-4 [grid-area:sfo]">
                            <div className="flex flex-col gap-1 w-full text-left">
                                <div className={cn('value leading-none text-white text-sm')}>
                                    {valueSuffixConstructor(
                                        formatNumberWithCommas(plan.debugData.bunkerVlsfoCost),
                                        `${currencySign}/MT`,
                                        true,
                                        'max-md:hidden'
                                    )}
                                </div>
                                <label className={cn('block text-frGrey-400 h-3.5 truncate text-xs leading-none')}>
                                    <span className="max-md:hidden">Bunker (VLSFO)</span>
                                    <span className="max-md:inline hidden">
                                        VLSFO
                                        <span className="max-md:inline max-[640px]:hidden"> {currencySign}/MT</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                        <div className="pt-4 [grid-area:mgo]">
                            <div className="flex flex-col gap-1 w-full text-left">
                                <div className={cn('value leading-none text-white text-sm')}>
                                    {valueSuffixConstructor(
                                        formatNumberWithCommas(plan.debugData.bunkerLsmgoCost),
                                        `${currencySign}/MT`,
                                        true,
                                        'max-md:hidden'
                                    )}
                                </div>
                                <label className={cn('block text-frGrey-400 h-3.5 truncate text-xs leading-none')}>
                                    <span className="max-md:hidden">Bunker (LSMGO)</span>
                                    <span className="max-md:inline hidden">
                                        LSMGO
                                        <span className="max-md:inline max-[640px]:hidden"> {currencySign}/MT</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </>
                }
            >
                <div className="mt-4 border-t border-frGrey-700 pt-4 px-4 -mx-4">
                    <MobileDebugTable plan={plan} waterTypes={waterTypes} />

                    <div className="flex items-center justify-center pt-3">
                        <Link
                            href={`/freight/plan/${resultId}/${plan.id}/map`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Button
                                variant="icon"
                                size="iconText"
                                className="text-primary-200 hover:text-primary-100 py-px px-px hover:border-transparent"
                            >
                                <Icon icon="Map" className="h-6 w-6 mr-2" />
                                Map
                            </Button>
                        </Link>
                    </div>
                </div>

                <div className="mt-4 border-t border-frGrey-700 pt-4 px-4 -mx-4 flex gap-6">
                    <div className="font-light leading-none text-white">Totals</div>

                    <div className="grid grid-cols-2 w-full gap-4">
                        <DebugTableKeyVal
                            className="col-span-2"
                            label="Bunker VLSFO mts"
                            value={formatNumberWithCommas(plan.debugData.totalCost.bunkersInMts.vlsfo)}
                        />

                        <DebugTableKeyVal
                            label="Bunker LSMGO mts"
                            value={formatNumberWithCommas(plan.debugData.totalCost.bunkersInMts.lsmgo)}
                        />

                        <DebugTableKeyVal
                            label="Total days"
                            value={formatNumberWithCommas(plan.debugData.totalCost.costInDays)}
                            className="justify-self-end min-w-[75px]"
                        />

                        <DebugTableKeyVal
                            label="EU ETS carbon mts"
                            value={
                                <span className="flex gap-1 items-center">
                                    {formatNumberWithCommas(plan.debugData.applicableCO2EmittedWithinEUETS)}

                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <span>
                                                    <Icon icon="InfoCircleFilled" className="h-4 w-4" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent className="bg-frGrey-800 border-frGrey-500 max-w-60 font-normal text-sm">
                                                <p className="text-center">
                                                    Applicable CO<sub>2</sub> EU ETS
                                                </p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </span>
                            }
                        />

                        <DebugTableKeyVal
                            label="Total cost"
                            value={valueSuffixConstructor(
                                formatNumberWithCommas(plan.debugData.totalCost.sumInDollars ?? 0),
                                currencySign
                            )}
                            className="justify-self-end min-w-[75px]"
                        />
                    </div>
                </div>
            </MobileResultCard>
        );
    }

    return (
        <div
            className={cn('bg-frGrey-990 text-white', {
                'w-full -my-6 p-6 min-h-[calc(100vh-81px)]': fullPage,
                'rounded-lg overflow-y-scroll max-h-modalH': !fullPage,
            })}
        >
            {body}
        </div>
    );
}

export default function PlanDetailsPage({
    resultId,
    planId,
    fullPage,
    initialCurrency,
    backToQuotePage,
    bookable,
}: {
    resultId: string;
    planId: string;
    fullPage?: boolean;
    initialCurrency?: string;
    backToQuotePage?: boolean;
    bookable?: boolean;
}) {
    return (
        <WithQueryClient>
            <CalculatorCurrencyProvider initialCurrency={initialCurrency}>
                <FetcherContext.Provider value={reactQueryFetcher}>
                    <TooltipProvider>
                        <PlanDetailsPageInner
                            resultId={resultId}
                            planId={planId}
                            fullPage={fullPage}
                            backToQuotePage={backToQuotePage}
                            bookable={bookable}
                        />
                    </TooltipProvider>
                </FetcherContext.Provider>
            </CalculatorCurrencyProvider>
        </WithQueryClient>
    );
}
